/**
 * Real self-fetching per-employee leave-balances hook (#23311). Read-only
 * — mirrors `use_employee_deductions.ts`'s "per-employee resolver with
 * state-machine + DI seam" shape exactly, on top of the OAuth-allowlisted
 * `payroll.getLeaveBalances` query.
 *
 * ── Why this is read-only, and the write path lives in `use_leave_payout.ts`
 *
 * The balances read is reachable by BOTH read-scoped and write-scoped OAuth
 * grants ("write implies read", same as every other WagePro employer read).
 * The payout flow — estimate preview + real cash-disbursement mutation —
 * lives in its own hook `use_leave_payout.ts` so a read-only surface (e.g.
 * a report screen) can consume the balances without dragging the write
 * machinery in.
 *
 * ── entitlement_required vs error
 *
 * `payroll.getLeaveBalances` is a `payrollEntityProcedure` (gated by
 * `requirePayrollAddon`), so a missing `payroll` add-on grant resolves
 * `entitlement_required`, not `error` (classified via
 * `isPayrollAddonRequiredError`, same as `use_leave_requests.ts`). Every
 * OTHER FORBIDDEN (missing OAuth scope, null-audience token, no entity
 * access) resolves the generic `error` state.
 */
import * as React from 'react';
import { getTrpcClient, isPayrollAddonRequiredError } from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import {
  zero_fill_balances,
  type LeaveBalance,
  type RealLeaveBalanceRecord,
} from './wagepro_store_leave_balances.js';

/** Input every `payroll.getLeaveBalances` call needs. */
export interface LeaveBalancesFetchInput {
  firm_id: string;
  entity_id: string;
  employee_id: string;
}

/** DI seam — the ONE real read this module needs, isolated for unit tests. */
export interface LeaveBalancesFetchDeps {
  getBalances: (
    input: LeaveBalancesFetchInput,
  ) => Promise<RealLeaveBalanceRecord[]>;
}

function defaultLeaveBalancesFetchDeps(): LeaveBalancesFetchDeps {
  const client = getTrpcClient();
  return {
    getBalances: (input) => client.payroll.getLeaveBalances.query(input),
  };
}

export type LeaveBalancesLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): LeaveBalancesLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export type LeaveBalancesFetchResult =
  | { outcome: 'success'; balances: LeaveBalance[] }
  | { outcome: 'failure'; failure: LeaveBalancesLoadFailure };

/**
 * Resolves the per-employee balance rows and zero-fills them into the
 * stable "annual / sick / casual" shape. Exported (and DI'd via `deps`) so
 * classification and normalisation are unit-testable against a fake client.
 */
export async function fetchLeaveBalances(
  input: LeaveBalancesFetchInput,
  deps?: LeaveBalancesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<LeaveBalancesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveBalancesFetchDeps();
    const rows = await resolvedDeps.getBalances(input);
    return { outcome: 'success', balances: zero_fill_balances(rows) };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load leave balances:', err);
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type LeaveBalancesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; balances: LeaveBalance[] };

/**
 * Pure derivation, exported separately so it is unit-testable without
 * rendering React. Mirrors `resolveDeductionsState`'s precedence exactly.
 */
export function resolveLeaveBalancesState(input: {
  entityContext: OAuthEntityContext;
  balances: LeaveBalance[] | undefined;
  failure: LeaveBalancesLoadFailure | undefined;
}): LeaveBalancesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.balances !== undefined) {
    return { status: 'ready', balances: input.balances };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export interface UseLeaveBalancesResult {
  state: LeaveBalancesState;
  /** Convenience projection: `[]` for every non-`ready` state. */
  balances: LeaveBalance[];
  is_loading: boolean;
  retry: () => void;
}

/**
 * Fetches leave balances for a single employee. `employee_id = null`
 * short-circuits: no fetch is issued, and the state stays `loading` until
 * a real id is passed (mirrors `useEmployeeDeductions(null)` — never emits
 * a fabricated "ready with empty balances" result).
 */
export function useLeaveBalances(
  employee_id: string | null,
): UseLeaveBalancesResult {
  const entityContext = useOAuthEntityContext();
  const [balances, setBalances] = React.useState<LeaveBalance[] | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    LeaveBalancesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready' || employee_id === null) return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const empId: string = employee_id;
    let cancelled = false;
    setBalances(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchLeaveBalances(
        { firm_id: firmId, entity_id: entityId, employee_id: empId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setBalances(result.balances);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, employee_id, reloadToken]);

  const state = React.useMemo(
    () => resolveLeaveBalancesState({ entityContext, balances, failure }),
    [entityContext, balances, failure],
  );

  return {
    state,
    balances: state.status === 'ready' ? state.balances : [],
    is_loading: state.status === 'loading',
    retry: React.useCallback(() => {
      setReloadToken((token) => token + 1);
    }, []),
  };
}
